/**
 * ListingDetailPage - Redesigned property detail view
 *
 * Layout:
 * 1. Title & Price Card (full width)
 * 2. Photo Carousel with ScrollSpy tabs (full width, below title)
 * 3. 2-Column Grid:
 *    - Left (65%): Details, Description, Amenities, Map
 *    - Right (35%): Sticky Realtor Sidebar
 *
 * ScrollSpy Pattern:
 * - All images displayed in continuous carousel (not filtered)
 * - Section tabs sync bidirectionally with carousel position
 * - Tab click → jumps to first image of that section
 * - Swipe/scroll → updates active tab based on current image section
 * - Modal has independent scroll position, syncs back on close
 */
import { useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Home,
  Ruler,
  Calendar,
  CheckCircle,
  AlertCircle,
  Maximize2,
} from "lucide-react";
import { BasePage } from "@/shared/ui/layout";
import { Card, Button, Skeleton } from "@/shared/ui/components";
import {
  EmblaPhotoCarousel,
  PhotoModal,
  PropertyMapCard,
  RealtorSidebar,
} from "@/features/listings/components";
import { apiClient } from "@/shared/api/baseClient";
import {
  useTranslation,
  useDeduplicatedEffect,
  useCurrentLocale,
} from "@/shared/hooks";
import { RailwayLineLogo } from "@/features/geo/components";
import type { Listing } from "@/shared/types";

// Types
interface RealtorInfo {
  org_id: string;
  org_name: string;
  org_type: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  verification_status: string;
  agent_photo_url?: string | null;
}

interface ListingDetailResponse {
  listing: Listing;
  realtor: RealtorInfo;
}

// ScrollSpy: Image with section metadata for two-way binding
interface CarouselImageWithSection {
  url: string;
  alt: string;
  section: string;
}

export function ListingDetailPage() {
  const t = useTranslation();
  const currentLocale = useCurrentLocale();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<ListingDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Photo modal state
  const [photoModalOpen, setPhotoModalOpen] = useState(false);

  // Page carousel position (used when modal is closed)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Modal slide position (independent tracking for modal navigation)
  const [modalSlideIndex, setModalSlideIndex] = useState(0);

  // Extract available sections (sections with at least one image)
  const sections = useMemo(() => {
    if (!data?.listing) return [];
    return Object.keys(data.listing.media.images_by_section).filter(
      (section) => {
        const sectionImages = data.listing?.media.images_by_section[section];
        return sectionImages && sectionImages.length > 0;
      },
    );
  }, [data]);

  // ScrollSpy: Flatten all images into single continuous list with section metadata
  const allCarouselImages = useMemo((): CarouselImageWithSection[] => {
    if (!data?.listing || sections.length === 0) return [];

    const result: CarouselImageWithSection[] = [];
    for (const section of sections) {
      const sectionImages = data.listing.media.images_by_section[section] || [];
      sectionImages.forEach((img, idx) => {
        result.push({
          url: img.url,
          alt: img.caption?.[currentLocale] || `${section} photo ${idx + 1}`,
          section,
        });
      });
    }
    return result;
  }, [data, sections, currentLocale]);

  // ScrollSpy: Map section name to its first image index (for tab-click navigation)
  const sectionStartIndices = useMemo((): Record<string, number> => {
    if (!data?.listing || sections.length === 0) return {};

    const indices: Record<string, number> = {};
    let currentIndex = 0;

    for (const section of sections) {
      indices[section] = currentIndex;
      const sectionImages = data.listing.media.images_by_section[section] || [];
      currentIndex += sectionImages.length;
    }

    return indices;
  }, [data, sections]);

  // ScrollSpy: Derive active section from current page slide position
  const activeSection = useMemo((): string => {
    if (allCarouselImages.length === 0 || sections.length === 0) {
      return sections[0] || "";
    }

    const currentImage = allCarouselImages[currentSlideIndex];
    return currentImage?.section || sections[0] || "";
  }, [allCarouselImages, currentSlideIndex, sections]);

  // Derive active section for modal (based on modal position)
  const modalActiveSection = useMemo((): string => {
    if (allCarouselImages.length === 0 || sections.length === 0) {
      return sections[0] || "";
    }

    const currentImage = allCarouselImages[modalSlideIndex];
    return currentImage?.section || sections[0] || "";
  }, [allCarouselImages, modalSlideIndex, sections]);

  // Page carousel: Handle tab click - jump to first image of that section
  const handleSectionClick = useCallback(
    (section: string) => {
      const targetIndex = sectionStartIndices[section];
      if (targetIndex !== undefined) {
        setCurrentSlideIndex(targetIndex);
      }
    },
    [sectionStartIndices],
  );

  // Page carousel: Handle slide change
  const handleSlideChange = useCallback((index: number) => {
    setCurrentSlideIndex(index);
  }, []);

  // Modal: Handle slide change (independent tracking)
  const handleModalSlideChange = useCallback((index: number) => {
    setModalSlideIndex(index);
  }, []);

  // Modal: Handle section click - jump within modal
  const handleModalSectionClick = useCallback(
    (section: string) => {
      const targetIndex = sectionStartIndices[section];
      if (targetIndex !== undefined) {
        setModalSlideIndex(targetIndex);
      }
    },
    [sectionStartIndices],
  );

  // Open modal - initialize modal position from page carousel
  const handleOpenModal = useCallback(() => {
    setModalSlideIndex(currentSlideIndex);
    setPhotoModalOpen(true);
  }, [currentSlideIndex]);

  // Close modal - sync page carousel to modal position
  const handleCloseModal = useCallback(() => {
    setCurrentSlideIndex(modalSlideIndex);
    setPhotoModalOpen(false);
  }, [modalSlideIndex]);

  // Fetch listing data
  useDeduplicatedEffect(() => {
    const fetchListing = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get<ListingDetailResponse>(
          `/listings/${id}`,
        );
        setData(response);
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load listing";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const backButton = (
    <button
      onClick={handleBack}
      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
      aria-label="Back"
    >
      <ArrowLeft className="w-5 h-5 text-gray-600" />
    </button>
  );

  // Loading state
  if (loading) {
    return (
      <BasePage
        title="Loading..."
        maxWidth="full"
        background="gray"
        padding="md"
        headerVariant="solid"
        headerProps={{ leftContent: backButton }}
      >
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-96 w-full rounded-lg" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <Skeleton className="h-64 w-full" />
            </div>
            <div className="lg:col-span-1">
              <Skeleton className="h-96 w-full" />
            </div>
          </div>
        </div>
      </BasePage>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <BasePage
        title="Error"
        maxWidth="full"
        background="gray"
        padding="md"
        headerVariant="solid"
        headerProps={{ leftContent: backButton }}
      >
        <div className="max-w-7xl mx-auto">
          <Card
            variant="default"
            padding="lg"
            className="text-center border-red-200"
          >
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">
              {t("listingDetailNotFound")}
            </h2>
            <p className="text-gray-600 mb-4">
              {error || t("listingDetailNotAvailable")}
            </p>
            <Button variant="primary" onClick={handleBack}>
              {t("listingDetailGoBack")}
            </Button>
          </Card>
        </div>
      </BasePage>
    );
  }

  const { listing, realtor } = data;

  // Section display names
  const SECTION_LABELS: Record<string, string> = {
    exterior: t("detailPhotoExterior"),
    interior: t("detailPhotoInterior"),
    kitchen: t("detailPhotoKitchen"),
    bathroom: t("detailPhotoBathroom"),
    other: t("detailPhotoOther"),
  };

  return (
    <BasePage
      title={listing.title[currentLocale]}
      description={listing.description[currentLocale]}
      maxWidth="full"
      background="gray"
      padding="md"
      headerVariant="solid"
      headerProps={{ leftContent: backButton }}
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Title & Price Card */}
        <Card variant="default" padding="lg" className="shadow-sm">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {listing.title[currentLocale]}
          </h1>

          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-4xl font-bold text-primary-600">
              ¥{listing.financials.rent.amount.toLocaleString()}
            </span>
            <span className="text-gray-500">
              {t("listingDetailPricePerMonth")}
            </span>
          </div>

          {listing.financials.management_fee && (
            <p className="text-gray-600 mb-4">
              + ¥{listing.financials.management_fee.amount.toLocaleString()}{" "}
              {t("listingDetailManagementFee")}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full">
              <Home className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {t(
                  `filterProperty${listing.property.property_type.charAt(0).toUpperCase() + listing.property.property_type.slice(1)}` as any,
                )}
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full">
              <span className="text-sm font-medium text-gray-700">
                {t(
                  `filterLayout${listing.property.room_layout.replace("+", "Plus")}` as any,
                )}
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full">
              <Ruler className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {listing.property.area_sqm}㎡
              </span>
            </div>
            {listing.property.floor && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full">
                <span className="text-sm font-medium text-gray-700">
                  {listing.property.floor}F
                  {listing.property.total_floors &&
                    ` / ${listing.property.total_floors}F`}
                </span>
              </div>
            )}
            {listing.property.year_built && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full">
                <Calendar className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  {t("listingDetailBuilt")} {listing.property.year_built}
                </span>
              </div>
            )}
          </div>
        </Card>

        {/* Section Tabs (ScrollSpy) - shown when modal is closed */}
        {!photoModalOpen && sections.length > 1 && (
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
            {sections.map((section) => (
              <button
                key={section}
                onClick={() => handleSectionClick(section)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                  ${
                    activeSection === section
                      ? "bg-primary-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }
                `}
              >
                {SECTION_LABELS[section] || section}
              </button>
            ))}
          </div>
        )}

        {/* Photo Carousel - Always visible, with expand button in corner */}
        <div className="relative">
          {/* Main Carousel */}
          <EmblaPhotoCarousel
            images={allCarouselImages}
            initialSlide={currentSlideIndex}
            externalSlideIndex={currentSlideIndex}
            onSlideChange={handleSlideChange}
            mode="page"
          />

          {/* Expand button in top-right corner */}
          {allCarouselImages.length > 0 && (
            <button
              onClick={handleOpenModal}
              className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 transition-all hover:scale-105"
              aria-label="View fullscreen"
            >
              <Maximize2 className="w-4 h-4 text-gray-700" />
              <span className="text-sm font-medium text-gray-700">
                {t("expandFullscreen") || "Fullscreen"}
              </span>
            </button>
          )}
        </div>

        {/* Photo Modal (with independent scroll position) */}
        <PhotoModal
          images={allCarouselImages}
          initialIndex={modalSlideIndex}
          open={photoModalOpen}
          onClose={handleCloseModal}
          onSlideChange={handleModalSlideChange}
          sections={sections}
          activeSection={modalActiveSection}
          sectionLabels={SECTION_LABELS}
          onSectionClick={handleModalSectionClick}
        />

        {/* 2-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Main Content (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Property Details */}
            <Card variant="default" padding="lg" className="shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t("listingDetailPropertyDetails")}
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">
                    {t("listingDetailRoomLayout")}
                  </p>
                  <p className="font-medium text-gray-900">
                    {listing.property.room_layout}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">
                    {t("listingDetailArea")}
                  </p>
                  <p className="font-medium text-gray-900">
                    {listing.property.area_sqm}㎡
                  </p>
                </div>
                {listing.financials.deposit && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      {t("listingDetailDeposit")}
                    </p>
                    <p className="font-medium text-gray-900">
                      ¥{listing.financials.deposit.amount.toLocaleString()}
                    </p>
                  </div>
                )}
                {listing.financials.key_money && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      {t("listingDetailKeyMoney")}
                    </p>
                    <p className="font-medium text-gray-900">
                      ¥{listing.financials.key_money.amount.toLocaleString()}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500 mb-1">
                    {t("listingDetailGuarantorRequired")}
                  </p>
                  <p className="font-medium text-gray-900">
                    {listing.financials.guarantor_required
                      ? t("listingDetailYes")
                      : t("listingDetailNo")}
                  </p>
                </div>
              </div>
            </Card>

            {/* Description */}
            {listing.description && (
              <Card variant="default" padding="lg" className="shadow-sm">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {t("listingDetailDescription")}
                </h2>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {listing.description[currentLocale]}
                </p>
              </Card>
            )}

            {/* Amenities - Flatten all nested amenity categories */}
            <Card variant="default" padding="lg" className="shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t("listingDetailAmenities")}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* Collect all amenities from nested structure */}
                {Object.entries(listing.amenities)
                  .flatMap(([category, categoryAmenities]) =>
                    Object.entries(categoryAmenities as Record<string, boolean>)
                      .filter(([_, value]) => value === true)
                      .map(([amenityKey]) => ({
                        key: `${category}_${amenityKey}`,
                        label: amenityKey,
                      })),
                  )
                  .map((amenity) => (
                    <div key={amenity.key} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {t(
                          `filter${amenity.label
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                            )
                            .join("")}` as any,
                        )}
                      </span>
                    </div>
                  ))}
              </div>
            </Card>

            {/* Location & Station Access */}
            {listing.location.coordinates && (
              <Card variant="default" padding="lg" className="shadow-sm">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {t("listingDetailLocation")}
                </h2>
                <p className="text-gray-700 mb-4">
                  {listing.location.address_display[currentLocale]}
                </p>

                {/* Nearest Stations */}
                {listing.location.stations &&
                  listing.location.stations.length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                        {t("listingDetailNearestStations")}
                      </h3>
                      <div className="space-y-2">
                        {listing.location.stations.map((station, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 text-sm"
                          >
                            {station.line_code && station.line_color ? (
                              <RailwayLineLogo
                                lineCode={station.line_code}
                                color={station.line_color}
                                size={16}
                              />
                            ) : (
                              <svg
                                className="w-4 h-4 text-primary-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                            )}
                            <span className="font-medium text-gray-900">
                              {station.name[currentLocale]}
                            </span>
                            {station.line_name && (
                              <span className="text-gray-500">
                                {" "}
                                ({station.line_name[currentLocale]})
                              </span>
                            )}
                            <span className="text-gray-600">
                              • {station.walk_minutes}{" "}
                              {t("listingDetailMinWalk")}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Map */}
                <PropertyMapCard
                  geo={listing.location.coordinates}
                  address={listing.location.address_display[currentLocale]}
                  station={listing.location.stations[0]?.name[currentLocale]}
                  walkMinutes={listing.location.stations[0]?.walk_minutes}
                  lineName={
                    listing.location.stations[0]?.line_name?.[currentLocale]
                  }
                />
              </Card>
            )}
          </div>

          {/* Right Column: Sticky Realtor Sidebar (1/3 width) */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6">
              <RealtorSidebar realtor={realtor} />
            </div>
          </div>
        </div>
      </div>
    </BasePage>
  );
}
